import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, GripVertical, Image, Edit2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AboutPagePanel = ({ token }) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // State for each section
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    story_title: '',
    story_intro: '',
    mission: '',
    vision: '',
    culture_title: '',
    culture_description: ''
  });
  
  const [timeline, setTimeline] = useState([]);
  const [values, setValues] = useState([]);
  const [cultureImages, setCultureImages] = useState([]);
  const [stats, setStats] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Edit states
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState(null);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [settingsRes, timelineRes, valuesRes, cultureRes, statsRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/api/cms/about-settings`, { headers }),
        fetch(`${API_URL}/api/cms/timeline-milestones`, { headers }),
        fetch(`${API_URL}/api/cms/core-values`, { headers }),
        fetch(`${API_URL}/api/cms/culture-images`, { headers }),
        fetch(`${API_URL}/api/cms/company-stats`, { headers }),
        fetch(`${API_URL}/api/cms/team-members`, { headers })
      ]);
      
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
      }
      if (timelineRes.ok) setTimeline(await timelineRes.json());
      if (valuesRes.ok) setValues(await valuesRes.json());
      if (cultureRes.ok) setCultureImages(await cultureRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (teamRes.ok) setTeamMembers(await teamRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/about-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Settings saved successfully');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Generic CRUD operations
  const createItem = async (endpoint, data, setState) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        const newItem = await res.json();
        setState(prev => [...prev, newItem]);
        toast.success('Item created successfully');
        setNewItem(null);
        return true;
      } else {
        throw new Error('Failed to create');
      }
    } catch (error) {
      toast.error('Failed to create item');
      return false;
    }
  };

  const updateItem = async (endpoint, id, data, setState) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/${endpoint}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setState(prev => prev.map(item => item.id === id ? updated : item));
        toast.success('Item updated successfully');
        setEditingItem(null);
        return true;
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update item');
      return false;
    }
  };

  const deleteItem = async (endpoint, id, setState) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/cms/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setState(prev => prev.filter(item => item.id !== id));
        toast.success('Item deleted successfully');
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-8 h-8 border-4 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="about-page-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">About Page</h2>
          <p className="text-gray-500">Manage your About page content</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="values">Core Values</TabsTrigger>
          <TabsTrigger value="culture">Culture</TabsTrigger>
          <TabsTrigger value="stats">Stats</TabsTrigger>
          <TabsTrigger value="team">Leadership</TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Hero Section</CardTitle>
              <CardDescription>Configure the hero section of the About page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hero Title</label>
                <Input
                  value={settings.hero_title}
                  onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                  placeholder="We are Navaris Digital"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hero Subtitle</label>
                <Textarea
                  value={settings.hero_subtitle}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                  placeholder="Transforming ideas into digital excellence..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hero Image URL</label>
                <Input
                  value={settings.hero_image}
                  onChange={(e) => setSettings({ ...settings, hero_image: e.target.value })}
                  placeholder="https://..."
                />
                {settings.hero_image && (
                  <img src={settings.hero_image} alt="Preview" className="mt-2 h-32 object-cover rounded" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Mission & Vision</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mission</label>
                <Textarea
                  value={settings.mission}
                  onChange={(e) => setSettings({ ...settings, mission: e.target.value })}
                  placeholder="Our mission statement..."
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vision</label>
                <Textarea
                  value={settings.vision}
                  onChange={(e) => setSettings({ ...settings, vision: e.target.value })}
                  placeholder="Our vision statement..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Our Story Section</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                <Input
                  value={settings.story_title}
                  onChange={(e) => setSettings({ ...settings, story_title: e.target.value })}
                  placeholder="Our Story"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Introduction Text</label>
                <Textarea
                  value={settings.story_intro}
                  onChange={(e) => setSettings({ ...settings, story_intro: e.target.value })}
                  placeholder="From a small team..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Culture Section</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                <Input
                  value={settings.culture_title}
                  onChange={(e) => setSettings({ ...settings, culture_title: e.target.value })}
                  placeholder="Our Culture"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Textarea
                  value={settings.culture_description}
                  onChange={(e) => setSettings({ ...settings, culture_description: e.target.value })}
                  placeholder="We believe in fostering..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Core Values Section</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Image URL</label>
                <Input
                  value={settings.core_values_bg_image || ''}
                  onChange={(e) => setSettings({ ...settings, core_values_bg_image: e.target.value })}
                  placeholder="https://..."
                />
                {settings.core_values_bg_image && (
                  <img src={settings.core_values_bg_image} alt="Preview" className="mt-2 h-32 object-cover rounded" />
                )}
              </div>
            </CardContent>
          </Card>

          <Button onClick={saveSettings} disabled={saving} className="w-full">
            <Save className="mr-2" size={16} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Timeline Milestones</h3>
            <Button 
              onClick={() => setNewItem({ year: '', title: '', description: '', image: '', order: timeline.length })}
              size="sm"
            >
              <Plus className="mr-1" size={16} /> Add Milestone
            </Button>
          </div>

          {/* New Item Form */}
          {newItem && (
            <Card className="border-2 border-dashed border-[#320547]">
              <CardContent className="pt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Year</label>
                    <Input
                      value={newItem.year}
                      onChange={(e) => setNewItem({ ...newItem, year: e.target.value })}
                      placeholder="2015"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Order</label>
                    <Input
                      type="number"
                      value={newItem.order}
                      onChange={(e) => setNewItem({ ...newItem, order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <Input
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Milestone title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="What happened during this milestone..."
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => createItem('timeline-milestones', newItem, setTimeline)}
                    size="sm"
                  >
                    <Save className="mr-1" size={16} /> Save
                  </Button>
                  <Button variant="outline" onClick={() => setNewItem(null)} size="sm">
                    <X className="mr-1" size={16} /> Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline Items */}
          {timeline.sort((a, b) => a.order - b.order).map((item) => (
            <Card key={item.id} className="relative">
              <CardContent className="pt-4">
                {editingItem?.id === item.id ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Year</label>
                        <Input
                          value={editingItem.year}
                          onChange={(e) => setEditingItem({ ...editingItem, year: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Order</label>
                        <Input
                          type="number"
                          value={editingItem.order}
                          onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Title</label>
                      <Input
                        value={editingItem.title}
                        onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <Textarea
                        value={editingItem.description}
                        onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => updateItem('timeline-milestones', item.id, editingItem, setTimeline)}
                        size="sm"
                      >
                        <Save className="mr-1" size={16} /> Save
                      </Button>
                      <Button variant="outline" onClick={() => setEditingItem(null)} size="sm">
                        <X className="mr-1" size={16} /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-[#320547] text-white text-sm font-semibold rounded-full">
                          {item.year}
                        </span>
                        <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      </div>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEditingItem(item)}>
                        <Edit2 size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700"
                        onClick={() => deleteItem('timeline-milestones', item.id, setTimeline)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {timeline.length === 0 && !newItem && (
            <div className="text-center py-12 text-gray-500">
              <p>No milestones yet. Click "Add Milestone" to create one.</p>
            </div>
          )}
        </TabsContent>

        {/* Core Values Tab */}
        <TabsContent value="values" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Core Values</h3>
            <Button 
              onClick={() => setNewItem({ title: '', description: '', icon: 'Star', order: values.length })}
              size="sm"
            >
              <Plus className="mr-1" size={16} /> Add Value
            </Button>
          </div>

          {/* New Item Form */}
          {newItem && activeTab === 'values' && (
            <Card className="border-2 border-dashed border-[#320547]">
              <CardContent className="pt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title</label>
                    <Input
                      value={newItem.title}
                      onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                      placeholder="Innovation First"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Icon (Lucide name)</label>
                    <Input
                      value={newItem.icon}
                      onChange={(e) => setNewItem({ ...newItem, icon: e.target.value })}
                      placeholder="Star, Target, Award..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="What this value means to us..."
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => createItem('core-values', newItem, setValues)}
                    size="sm"
                  >
                    <Save className="mr-1" size={16} /> Save
                  </Button>
                  <Button variant="outline" onClick={() => setNewItem(null)} size="sm">
                    <X className="mr-1" size={16} /> Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Values List */}
          <div className="grid md:grid-cols-2 gap-4">
            {values.sort((a, b) => a.order - b.order).map((item) => (
              <Card key={item.id}>
                <CardContent className="pt-4">
                  {editingItem?.id === item.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Title</label>
                          <Input
                            value={editingItem.title}
                            onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Icon</label>
                          <Input
                            value={editingItem.icon}
                            onChange={(e) => setEditingItem({ ...editingItem, icon: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <Textarea
                          value={editingItem.description}
                          onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => updateItem('core-values', item.id, editingItem, setValues)}
                          size="sm"
                        >
                          <Save className="mr-1" size={16} /> Save
                        </Button>
                        <Button variant="outline" onClick={() => setEditingItem(null)} size="sm">
                          <X className="mr-1" size={16} /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[#320547] font-semibold">{item.icon}</span>
                          <h4 className="font-semibold text-gray-900">{item.title}</h4>
                        </div>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingItem(item)}>
                          <Edit2 size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600"
                          onClick={() => deleteItem('core-values', item.id, setValues)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {values.length === 0 && !newItem && (
            <div className="text-center py-12 text-gray-500">
              <p>No values yet. Click "Add Value" to create one.</p>
            </div>
          )}
        </TabsContent>

        {/* Culture Images Tab */}
        <TabsContent value="culture" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Culture Images</h3>
            <Button 
              onClick={() => setNewItem({ image_url: '', title: '', caption: '', order: cultureImages.length })}
              size="sm"
            >
              <Plus className="mr-1" size={16} /> Add Image
            </Button>
          </div>

          {/* New Item Form */}
          {newItem && activeTab === 'culture' && (
            <Card className="border-2 border-dashed border-[#320547]">
              <CardContent className="pt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Image URL</label>
                  <Input
                    value={newItem.image_url}
                    onChange={(e) => setNewItem({ ...newItem, image_url: e.target.value })}
                    placeholder="https://..."
                  />
                  {newItem.image_url && (
                    <img src={newItem.image_url} alt="Preview" className="mt-2 h-32 object-cover rounded" />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Title (shown on hover)</label>
                  <Input
                    value={newItem.title || ''}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Team Collaboration"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subtext (shown on hover)</label>
                  <Input
                    value={newItem.caption || ''}
                    onChange={(e) => setNewItem({ ...newItem, caption: e.target.value })}
                    placeholder="Life at Navaris Digital"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => createItem('culture-images', newItem, setCultureImages)}
                    size="sm"
                  >
                    <Save className="mr-1" size={16} /> Save
                  </Button>
                  <Button variant="outline" onClick={() => setNewItem(null)} size="sm">
                    <X className="mr-1" size={16} /> Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Images Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {cultureImages.sort((a, b) => a.order - b.order).map((item) => (
              <Card key={item.id} className="overflow-hidden">
                <div className="relative aspect-square">
                  <img 
                    src={item.image_url} 
                    alt={item.title || item.caption || 'Culture'} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => setEditingItem(item)}
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => deleteItem('culture-images', item.id, setCultureImages)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                <div className="p-2">
                  {item.title && <p className="text-sm font-medium text-gray-900">{item.title}</p>}
                  {item.caption && <p className="text-xs text-gray-500">{item.caption}</p>}
                </div>
              </Card>
            ))}
          </div>

          {/* Edit Modal */}
          {editingItem && activeTab === 'culture' && (
            <Card className="border-2 border-[#320547]">
              <CardContent className="pt-4 space-y-4">
                <h4 className="font-semibold">Edit Image</h4>
                <div>
                  <label className="block text-sm font-medium mb-1">Image URL</label>
                  <Input
                    value={editingItem.image_url}
                    onChange={(e) => setEditingItem({ ...editingItem, image_url: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Title (shown on hover)</label>
                  <Input
                    value={editingItem.title || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subtext (shown on hover)</label>
                  <Input
                    value={editingItem.caption || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, caption: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => updateItem('culture-images', editingItem.id, editingItem, setCultureImages)}
                    size="sm"
                  >
                    <Save className="mr-1" size={16} /> Save
                  </Button>
                  <Button variant="outline" onClick={() => setEditingItem(null)} size="sm">
                    <X className="mr-1" size={16} /> Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {cultureImages.length === 0 && !newItem && (
            <div className="text-center py-12 text-gray-500">
              <p>No images yet. Click "Add Image" to create one.</p>
            </div>
          )}
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Company Stats</h3>
            <Button 
              onClick={() => setNewItem({ value: '', label: '', icon: 'TrendingUp', order: stats.length })}
              size="sm"
            >
              <Plus className="mr-1" size={16} /> Add Stat
            </Button>
          </div>

          {/* New Item Form */}
          {newItem && activeTab === 'stats' && (
            <Card className="border-2 border-dashed border-[#320547]">
              <CardContent className="pt-4 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Value</label>
                    <Input
                      value={newItem.value}
                      onChange={(e) => setNewItem({ ...newItem, value: e.target.value })}
                      placeholder="200+"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Label</label>
                    <Input
                      value={newItem.label}
                      onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                      placeholder="Projects Delivered"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Icon</label>
                    <Input
                      value={newItem.icon}
                      onChange={(e) => setNewItem({ ...newItem, icon: e.target.value })}
                      placeholder="TrendingUp"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => createItem('company-stats', newItem, setStats)}
                    size="sm"
                  >
                    <Save className="mr-1" size={16} /> Save
                  </Button>
                  <Button variant="outline" onClick={() => setNewItem(null)} size="sm">
                    <X className="mr-1" size={16} /> Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats List */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.sort((a, b) => a.order - b.order).map((item) => (
              <Card key={item.id}>
                <CardContent className="pt-4">
                  {editingItem?.id === item.id ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Value</label>
                        <Input
                          value={editingItem.value}
                          onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Label</label>
                        <Input
                          value={editingItem.label}
                          onChange={(e) => setEditingItem({ ...editingItem, label: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Icon</label>
                        <Input
                          value={editingItem.icon}
                          onChange={(e) => setEditingItem({ ...editingItem, icon: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => updateItem('company-stats', item.id, editingItem, setStats)}
                          size="sm"
                        >
                          <Save className="mr-1" size={16} /> Save
                        </Button>
                        <Button variant="outline" onClick={() => setEditingItem(null)} size="sm">
                          <X className="mr-1" size={16} /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-3xl font-bold text-[#320547] mb-1">{item.value}</div>
                      <div className="text-gray-600 text-sm">{item.label}</div>
                      <div className="text-gray-400 text-xs mt-1">{item.icon}</div>
                      <div className="flex justify-center gap-2 mt-3">
                        <Button variant="ghost" size="sm" onClick={() => setEditingItem(item)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600"
                          onClick={() => deleteItem('company-stats', item.id, setStats)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {stats.length === 0 && !newItem && (
            <div className="text-center py-12 text-gray-500">
              <p>No stats yet. Click "Add Stat" to create one.</p>
            </div>
          )}
        </TabsContent>

        {/* Team/Leadership Tab */}
        <TabsContent value="team" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Leadership Team</h3>
              <p className="text-sm text-gray-500">Manage team members shown on the About page. Leave empty to hide the section.</p>
            </div>
            <Button 
              onClick={() => setNewItem({ 
                name: '', 
                position: '', 
                photo: '', 
                bio: '', 
                linkedin: '', 
                is_leadership: true, 
                is_active: true,
                order: teamMembers.length 
              })}
              size="sm"
            >
              <Plus className="mr-1" size={16} /> Add Team Member
            </Button>
          </div>

          {/* New Team Member Form */}
          {newItem && activeTab === 'team' && (
            <Card className="border-2 border-dashed border-[#320547]">
              <CardContent className="pt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name *</label>
                    <Input
                      value={newItem.name}
                      onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Position *</label>
                    <Input
                      value={newItem.position}
                      onChange={(e) => setNewItem({ ...newItem, position: e.target.value })}
                      placeholder="CEO"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Photo URL</label>
                  <Input
                    value={newItem.photo}
                    onChange={(e) => setNewItem({ ...newItem, photo: e.target.value })}
                    placeholder="https://..."
                  />
                  {newItem.photo && (
                    <img src={newItem.photo} alt="Preview" className="mt-2 h-20 w-20 object-cover rounded-full" />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bio</label>
                  <Textarea
                    value={newItem.bio}
                    onChange={(e) => setNewItem({ ...newItem, bio: e.target.value })}
                    placeholder="Brief description..."
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">LinkedIn URL</label>
                  <Input
                    value={newItem.linkedin}
                    onChange={(e) => setNewItem({ ...newItem, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      if (newItem.name && newItem.position) {
                        createItem('team-members', newItem, setTeamMembers);
                      } else {
                        toast.error('Name and Position are required');
                      }
                    }}
                    size="sm"
                  >
                    <Save className="mr-1" size={16} /> Save
                  </Button>
                  <Button variant="outline" onClick={() => setNewItem(null)} size="sm">
                    <X className="mr-1" size={16} /> Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Team Members List */}
          <div className="grid gap-4">
            {teamMembers.map((member, index) => (
              <Card key={member.id} className="overflow-hidden">
                <CardContent className="p-4">
                  {editingItem?.id === member.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Name</label>
                          <Input
                            value={editingItem.name}
                            onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Position</label>
                          <Input
                            value={editingItem.position}
                            onChange={(e) => setEditingItem({ ...editingItem, position: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Photo URL</label>
                        <Input
                          value={editingItem.photo}
                          onChange={(e) => setEditingItem({ ...editingItem, photo: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Bio</label>
                        <Textarea
                          value={editingItem.bio || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, bio: e.target.value })}
                          rows={2}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">LinkedIn URL</label>
                        <Input
                          value={editingItem.linkedin || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, linkedin: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => updateItem('team-members', member.id, editingItem, setTeamMembers)}
                          size="sm"
                        >
                          <Save className="mr-1" size={16} /> Save
                        </Button>
                        <Button variant="outline" onClick={() => setEditingItem(null)} size="sm">
                          <X className="mr-1" size={16} /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      {member.photo ? (
                        <img src={member.photo} alt={member.name} className="w-16 h-16 rounded-full object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                          No Photo
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#320547]">{member.name}</h4>
                        <p className="text-sm text-[#95228A]">{member.position}</p>
                        {member.bio && <p className="text-sm text-gray-500 mt-1 line-clamp-1">{member.bio}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingItem(member)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteItem('team-members', member.id, setTeamMembers)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {teamMembers.length === 0 && !newItem && (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
              <p className="mb-2">No team members yet.</p>
              <p className="text-sm">The Leadership section will be hidden on the About page until you add team members.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AboutPagePanel;
